'use client';

import type {
  ProductDetailData,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { VariantStock } from './variant-stock';
import Link from '@eliadmin/core/src/components/link';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

export function DesktopTable({
  variants,
  product,
}: {
  variants: VariantsEntity[];
  product: ProductDetailData;
}) {
  const setSelectedVariant = useVariantsStore(state => state.setSelectedVariant);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const { t } = useTranslations();
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);

  const sortedVariants = variants.map(variant => ({
    ...variant,
    values: Array.isArray(variant.values)
      ? [...variant.values].sort((a, b) => a.attribute_position - b.attribute_position)
      : [],
  }));

  const dynamicHeadings =
    sortedVariants[0] && Array.isArray(sortedVariants[0].values)
      ? sortedVariants[0].values.slice(0, 4).map(value => value.attribute_name)
      : [];

  const tableHeadings = [
    t('text552'),
    `${t('text1462')} (cm)`,
    ...dynamicHeadings,
    t('text164'),
    t('text3605'),
  ] as string[];

  const getColumnClasses = (index: number) => {
    const baseClasses = 'flex h-16 items-center justify-start px-2';
    const widthClasses = {
      0: 'min-w-[150px] max-w-[150px]',
      1: 'min-w-[100px] max-w-[100px]',
      2: 'min-w-[180px] max-w-[180px]',
      3: 'min-w-[100px] max-w-[100px]',
      4: 'min-w-[100px] max-w-[100px]',
      5: 'min-w-[100px] max-w-[100px]',
      6: 'min-w-[100px] max-w-[100px]',
      7: 'min-w-[400px]',
    };
    return cn(baseClasses, widthClasses[index as keyof typeof widthClasses] || '');
  };

  const getRowColumnClasses = (index: number) => {
    const baseClasses = 'flex h-[70px] items-center justify-start px-2 text-sm';
    const widthClasses = {
      0: 'min-w-[150px] max-w-[150px]',
      1: 'min-w-[100px] max-w-[100px]',
      2: 'min-w-[180px] max-w-[180px]',
      3: 'min-w-[100px] max-w-[100px]',
      4: 'min-w-[100px] max-w-[100px]',
      5: 'min-w-[100px] max-w-[100px]',
      6: 'min-w-[100px] max-w-[100px]',
      7: 'min-w-[400px]',
    };
    return cn(baseClasses, widthClasses[index as keyof typeof widthClasses] || '');
  };

  return (
    <div className="hidden w-full flex-col items-start justify-start gap-0 overflow-x-auto sm:flex">
      <div className="flex w-full items-center justify-start bg-[#F5F5F5]">
        {tableHeadings.map((heading, index) => (
          <div key={heading} className={cn(getColumnClasses(index), 'bg-[#F5F5F5] py-3')}>
            <span className="text-balance text-[13px] font-medium text-foreground">{heading}</span>
          </div>
        ))}
      </div>
      {sortedVariants.map(variant => {
        const priceDisplay = getProductPriceDisplay(variant, { isWholesaler });

        return (
          <div
            key={variant.id}
            className={cn('flex w-full items-center justify-start border-b border-b-[#E9E9E9]')}
          >
            <div
              className={cn(
                getRowColumnClasses(0),
                'cursor-pointer uppercase text-primary underline',
                selectedVariant?.id === variant.id && 'font-bold',
              )}
              onClick={() => {
                setSelectedVariant(variant);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {variant.name || variant.code}
            </div>
            <div className={cn(getRowColumnClasses(1), 'text-foreground')}>
              {(() => {
                const dimensions = [variant.width, variant.height, variant.length]
                  .filter(dim => dim !== undefined && dim !== null && dim !== '')
                  .map(dim => String(dim))
                  .join('x');
                return dimensions || '--';
              })()}
            </div>
            <div className={cn(getRowColumnClasses(2), 'text-foreground')}>
              {variant.values?.[0]?.name ?? ''}
            </div>
            <div className={cn(getRowColumnClasses(3), 'text-foreground')}>
              {variant.values?.[1]?.name ?? ''}
            </div>
            <div className={cn(getRowColumnClasses(6), 'text-foreground')}>
              {priceDisplay.priceOnRequest
                ? t('text533')
                : formatPrice(priceDisplay.currentWithVat, currency, currencySettings)}
            </div>
            <VariantStock variant={variant} />
          </div>
        );
      })}
    </div>
  );
}
